import { ref } from 'vue'

const TEST_DATE_KEY = 'lex_test_date'
const TEST_MODE_KEY = 'lex_test_mode'

/**
 * Override reattivo dell'"adesso" usato dalla modalità test.
 * Stringa 'YYYY-MM-DDTHH:mm' (data+ora) — o il vecchio 'YYYY-MM-DD' — oppure null (= ora reale).
 */
export const testDate = ref(localStorage.getItem(TEST_DATE_KEY) || null)

/**
 * La modalità test è disponibile nelle build di sviluppo, oppure quando
 * abilitata esplicitamente via localStorage (`lex_test_mode = '1'`),
 * così è attivabile anche nella build servita dal backend.
 */
export const isTestModeAvailable = import.meta.env.DEV || localStorage.getItem(TEST_MODE_KEY) === '1'

/**
 * Imposta (o azzera) la data di test. Passa null per tornare alla data reale.
 * @param {string|null} dateStr - Data 'YYYY-MM-DD' o null
 */
export function setTestDate(dateStr) {
  if (dateStr) {
    testDate.value = dateStr
    localStorage.setItem(TEST_DATE_KEY, dateStr)
  } else {
    testDate.value = null
    localStorage.removeItem(TEST_DATE_KEY)
  }
}

/**
 * Restituisce la data "adesso", rispettando l'eventuale data di test
 * (alla mezzanotte locale). Usare al posto di `new Date()` per i confronti
 * con "oggi", così la modalità test può sovrascriverla.
 * @returns {Date}
 */
export function now() {
  const v = testDate.value
  if (v) {
    if (v.includes('T')) {
      // datetime-local 'YYYY-MM-DDTHH:mm' → interpretato come ora locale
      const d = new Date(v)
      if (!isNaN(d.getTime())) return d
    } else {
      // retrocompatibilità con il vecchio formato data 'YYYY-MM-DD' (mezzanotte locale)
      const [y, m, day] = v.split('-').map(Number)
      if (y && m && day) return new Date(y, m - 1, day)
    }
  }
  return new Date()
}

/**
 * Restituisce "oggi" come stringa 'YYYY-MM-DD' in fuso orario locale,
 * rispettando la data di test.
 * @returns {string}
 */
export function todayStr() {
  const t = now()
  return t.getFullYear() + '-' +
    String(t.getMonth() + 1).padStart(2, '0') + '-' +
    String(t.getDate()).padStart(2, '0')
}

/**
 * Tick reattivo aggiornato ogni minuto: i computed che lo leggono si
 * ricalcolano da soli, così i countdown delle deadline restano "vivi".
 */
export const clockTick = ref(0)
let _clockInterval = null

/** Avvia il timer condiviso (idempotente). Chiamare una volta all'avvio dell'app. */
export function startClock() {
  if (_clockInterval) return
  _clockInterval = setInterval(() => { clockTick.value++ }, 60000)
}

/**
 * Converte un valore data/ora (ISO o stringa data) nel formato richiesto da
 * un input `datetime-local`: 'YYYY-MM-DDTHH:mm' in fuso orario locale.
 * @param {string} value
 * @returns {string}
 */
export function toDatetimeLocalInput(value) {
  if (!value) return ''
  const d = new Date(value)
  if (isNaN(d.getTime())) return ''
  return d.getFullYear() + '-' +
    String(d.getMonth() + 1).padStart(2, '0') + '-' +
    String(d.getDate()).padStart(2, '0') + 'T' +
    String(d.getHours()).padStart(2, '0') + ':' +
    String(d.getMinutes()).padStart(2, '0')
}

/**
 * Calcola lo stato di urgenza di una deadline (istante preciso).
 * Reattivo a `clockTick` (timer al minuto) e a `testDate` (modalità test).
 *
 * @param {string} deadlineIso - Istante della scadenza (ISO o datetime-local)
 * @param {{warnHours?: number, criticalHours?: number}} [thresholds]
 * @param {boolean} [done] - Scadenza già evasa
 * @returns {{ level: 'none'|'safe'|'warn'|'critical'|'overdue', msRemaining: number|null }}
 */
export function getDeadlineInfo(deadlineIso, thresholds = {}, done = false) {
  // Tocca le dipendenze reattive così i computed che usano questa funzione si aggiornano
  // eslint-disable-next-line no-unused-expressions
  clockTick.value
  if (done || !deadlineIso) return { level: 'none', msRemaining: null }

  const deadline = new Date(deadlineIso)
  if (isNaN(deadline.getTime())) return { level: 'none', msRemaining: null }

  const warnHours = thresholds.warnHours ?? 24
  const criticalHours = thresholds.criticalHours ?? 4

  const msRemaining = deadline.getTime() - now().getTime()
  const hoursRemaining = msRemaining / 3600000

  let level
  if (msRemaining <= 0) level = 'overdue'
  else if (hoursRemaining <= criticalHours) level = 'critical'
  else if (hoursRemaining <= warnHours) level = 'warn'
  else level = 'safe'

  return { level, msRemaining }
}

/**
 * Istante di scadenza effettivo per una deadline relativa: valore base + offsetDays giorni.
 * Usato per scadenze derivate dal campo stesso (es. la DUE scade 15 giorni dopo la data di registro).
 * @param {string|null|undefined} baseValue - data/datetime base (ISO o datetime-local)
 * @param {number} [offsetDays] - giorni da aggiungere alla base (0 = nessun offset)
 * @returns {string|null} ISO dell'istante effettivo, o null se base mancante/invalida
 */
export function effectiveDeadline(baseValue, offsetDays = 0) {
  if (!baseValue) return null
  const d = new Date(baseValue)
  if (isNaN(d.getTime())) return null
  if (offsetDays) d.setDate(d.getDate() + offsetDays)
  return d.toISOString()
}

/**
 * Etichetta breve del tempo rimanente/trascorso (es. "3h 20m", "2g 4h", "-15m").
 * @param {number|null} msRemaining
 * @returns {string}
 */
export function formatRemaining(msRemaining) {
  if (msRemaining === null || msRemaining === undefined) return ''
  const past = msRemaining < 0
  let totalMinutes = Math.floor(Math.abs(msRemaining) / 60000)
  const days = Math.floor(totalMinutes / 1440)
  totalMinutes -= days * 1440
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes - hours * 60

  let label
  if (days > 0) label = `${days}g ${hours}h`
  else if (hours > 0) label = `${hours}h ${minutes}m`
  else label = `${minutes}m`

  return past ? `-${label}` : label
}

/**
 * Aggiunge giorni utili (escludendo sabato e domenica) a una data
 * @param {Date} date - Data di partenza
 * @param {number} days - Numero di giorni utili da aggiungere
 * @returns {Date} - Nuova data con i giorni utili aggiunti
 */
export function addBusinessDays(date, days) {
  const result = new Date(date)
  let addedDays = 0
  
  while (addedDays < days) {
    result.setDate(result.getDate() + 1)
    
    // Ottieni il giorno della settimana (0 = domenica, 6 = sabato)
    const dayOfWeek = result.getDay()
    
    // Se non è sabato (6) né domenica (0), conta come giorno utile
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      addedDays++
    }
  }
  
  return result
}

/**
 * Calcola il numero di giorni utili tra due date (escludendo sabato e domenica)
 * @param {Date} startDate - Data di inizio
 * @param {Date} endDate - Data di fine
 * @returns {number} - Numero di giorni utili
 */
export function calculateBusinessDays(startDate, endDate) {
  // Crea copie delle date per non modificare gli originali
  const start = new Date(startDate)
  const end = new Date(endDate)
  
  // Reset delle ore per confronto accurato
  start.setHours(0, 0, 0, 0)
  end.setHours(0, 0, 0, 0)
  
  // Se le date sono uguali, ritorna 0
  if (start.getTime() === end.getTime()) {
    return 0
  }
  
  // Se la data di fine è prima della data di inizio, ritorna 0
  if (end < start) {
    return 0
  }
  
  let businessDays = 0
  const current = new Date(start)
  
  // Inizia dal giorno successivo alla data di inizio
  current.setDate(current.getDate() + 1)
  
  // Itera attraverso ogni giorno tra start e end (escludendo start, includendo end)
  while (current <= end) {
    const dayOfWeek = current.getDay()
    
    // Se non è sabato (6) né domenica (0), conta come giorno utile
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      businessDays++
    }
    
    current.setDate(current.getDate() + 1)
  }
  
  return businessDays
}

/**
 * Verifica se una data è un giorno utile (non sabato né domenica)
 * @param {Date} date - Data da verificare
 * @returns {boolean} - True se è un giorno utile
 */
export function isBusinessDay(date) {
  const dayOfWeek = date.getDay()
  return dayOfWeek !== 0 && dayOfWeek !== 6
}
